import { useEffect, useMemo, useState } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Separator } from "components/core/Seperator";
import { MixedSection } from "components/domain/contents/sections/MixedSection";
import { OriginalSection } from "components/domain/contents/sections/OriginalSection";
import { RecentSection } from "components/domain/contents/sections/RecentSection";
import {
  MixContentDocument,
  Mix_Content_Tag_Enum_Enum,
  SessionDocument,
  useGetUserInterestTagsQuery,
  useSessionQuery,
} from "graphql/generated";
import { fireEvent } from "lib/ga";
import { LIMIT, buildQueryVariables } from "utils/buildQueryVariables";
import { getClient } from "utils/client";
import { IndexPageContentSection } from "types/app";
import {
  DEFAULT_TAG_LIST,
  ORDERED_MIX_CONTENT_TAG_ENUM,
  SHINHAN_INVEST_TAG_LIST,
} from "utils/enum";
import { requiresShinhanInvestAccess, hasShinhanInvestAccess } from "utils/accessControl";

const Container = styled.div`
  --section-desktop-gap: 56px;
  --section-tablet-gap: 48px;
  --section-mobile-gap: 32px;

  display: flex;
  flex-direction: column;
`;

const SectionBox = styled.div`
  margin-bottom: var(--section-desktop-gap);

  @media (max-width: 1024px) {
    margin-bottom: var(--section-tablet-gap);
  }

  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const SectionSeperator = styled(Separator)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    width: auto;
    margin: var(--section-mobile-gap) calc(-1 * var(--page-padding));
  }
`;

const Page = () => {
  const router = useRouter();

  const { data: sessionData } = useSessionQuery();
  const userId = sessionData?.session?.me?.id;
  const userGroup = sessionData?.session?.me?.group_id;

  const { data: userInterestTagsData } = useGetUserInterestTagsQuery({
    variables: { userId: userId! },
    skip: !userId,
  });


  // Handle post-login redirect for Shinhan Investment content
  useEffect(() => {
    // Check if user just logged in and there's a redirect parameter
    if (sessionData?.session?.me && router.query.redirect) {
      const redirectUrl = decodeURIComponent(router.query.redirect as string);
      // Store the redirect URL and navigate to it
      router.push(redirectUrl);
    }
  }, [sessionData, router]);

  const tags = useMemo((): Mix_Content_Tag_Enum_Enum[] | null => {
    const { tag } = router.query;

    // 1. 쿼리에서 유효한 태그가 있으면 배열로 반환
    if (
      typeof tag === "string" &&
      ORDERED_MIX_CONTENT_TAG_ENUM.includes(tag as Mix_Content_Tag_Enum_Enum)
    ) {
      return [tag as Mix_Content_Tag_Enum_Enum];
    }

    // 2. 유저 관심 태그가 있으면, 해당 태그 리스트 반환
    const userTags = userInterestTagsData?.mix_user_interest_tag;
    if (userTags?.length) {
      return userTags.map(
        (item) => item.tag_value as Mix_Content_Tag_Enum_Enum,
      );
    }

    // 3. userGroup별 분기
    switch (userGroup) {
      case "master":
        return null; // 전체 보기(=null)
      case "shinhaninvest":
        return [
          ...DEFAULT_TAG_LIST,
          ...SHINHAN_INVEST_TAG_LIST,
        ] as Mix_Content_Tag_Enum_Enum[];
      case "general":
      case "thesmc":
      case undefined:
        return DEFAULT_TAG_LIST as Mix_Content_Tag_Enum_Enum[];
      default:
        return DEFAULT_TAG_LIST as Mix_Content_Tag_Enum_Enum[];
    }
  }, [router.query, userInterestTagsData, userGroup]);

  const [touched, setTouched] = useState<
    Record<IndexPageContentSection, boolean>
  >({
    original: false,
    recent: false,
    mixed: false,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const sections = ["original", "recent", "mixed"];
          const targetId = entry.target.getAttribute("data-observer-id");

          if (typeof targetId === "string" && sections.includes(targetId)) {
            const section = targetId as IndexPageContentSection;

            if (entry.isIntersecting && !touched[section]) {
              fireEvent({
                event: "scroll_depth",
                params: {
                  scrolled_section: section,
                },
              });

              setTouched((prev) => ({ ...prev, [section]: true }));
            }
          } else {
            throw new Error("targetId is not a section!");
          }
        }
      },
      {
        root: document.querySelector("index-page-container"),
        rootMargin: "0% 0px -100% 0px",
      },
    );

    const elements = document.querySelectorAll("[data-observer-id]");
    elements.forEach((el) => observer.observe(el));

    return () => elements.forEach((el) => observer.unobserve(el));
  }, [touched]);


  return (
    <>
      <Head>
        <link rel="canonical" href="https://mix.day/" />
      </Head>
      <Container id="index-page-container">
        {tags === null && (
          <>
            {/* OriginalSection : mobile only (<1024px) */}
            <SectionBox
              css={css`
                display: none;

                @media (max-width: 1023px) {
                  display: block;
                }
              `}
            >
              <OriginalSection
                data-observer-id="original"
                contentSection="original-main"
                variables={buildQueryVariables(
                  "original",
                  null,
                  LIMIT.original,
                )}
              />
            </SectionBox>
          </>
        )}
        {/* RecentSection : tablet, mobile */}
        <SectionBox
          css={css`
            display: none;

            @media (max-width: 1024px) {
              display: block;
            }
          `}
        >
          {tags === null && <SectionSeperator />}
          <RecentSection
            data-observer-id="recent"
            contentSection="recent"
            variables={buildQueryVariables("recent", tags, LIMIT.default)}
          />
        </SectionBox>
        {/* MixedSection : desktop only */}
        <SectionBox
          css={css`
            display: block;

            @media (max-width: 1024px) {
              display: none;
            }
          `}
        >
          <MixedSection
            data-observer-id="mixed"
            contentSection="mixed"
            variables={buildQueryVariables("recent", tags, LIMIT.default)}
          />
        </SectionBox>
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = getClient({
    headers: {
      cookie: ctx.req.headers.cookie,
    },
    ctx,
  });

  const tagFromQuery =
    ORDERED_MIX_CONTENT_TAG_ENUM.find((tag) => tag === ctx.query?.tag) ?? null;

  const tags = tagFromQuery ? [tagFromQuery] : null;

  // Server-side access control for Shinhan Investment exclusive content
  if (requiresShinhanInvestAccess(tagFromQuery ?? undefined)) {
    try {
      // Get session data to check user group
      const { data: sessionData } = await client.query({
        query: SessionDocument,
      });

      const userGroup = sessionData?.session?.me?.group_id;

      // If user is not logged in, redirect to login page with original URL stored
      if (!sessionData?.session?.me) {
        const originalUrl = `/?tag=${tagFromQuery}`;
        return {
          redirect: {
            destination: `/#signin?redirect=${encodeURIComponent(originalUrl)}`,
            permanent: false,
          },
        };
      }

      // If user doesn't have Shinhan Investment access, redirect to home
      if (!hasShinhanInvestAccess(userGroup)) {
        return {
          redirect: {
            destination: "/",
            permanent: false,
          },
        };
      }
    } catch (error) {
      // If session query fails, redirect to login page
      return {
        redirect: {
          destination: "/#signin",
          permanent: false,
        },
      };
    }
  }

  try {
    // 비로그인 상태이거나, 특정 태그가 지정된 경우의 초기 데이터만 미리 불러옵니다.
    await client.query({
      query: MixContentDocument,
      variables: buildQueryVariables("original", null, LIMIT.original),
    });

    await client.query({
      query: MixContentDocument,
      variables: buildQueryVariables("recent", tags, LIMIT.default),
    });

    // 세션 정보는 클라이언트에서 불러오므로, props로 넘기지 않습니다.
    return {
      props: {
        __APOLLO_CACHE_DATA__: client.extract(),
      },
    };
  } catch (error) {
    return {
      props: {
        __APOLLO_CACHE_DATA__: client.extract(),
      },
    };
  }
};

export default Page;
